import { Box, Button, LinearProgress, Paper, Slide, Snackbar, Typography } from "@mui/material";
import { Loader2, RotateCw, Sparkles } from "lucide-react";
import React, { useState } from "react";

import { VERSION_UPDATE_PROMPT_GRADIENT, VERSION_UPDATE_PROMPT_SPIN } from "constants/versionUpdatePrompt";
import { COMMON_COPY } from "copy/common";
import { useCopy } from "i18n/useCopy";

interface Props {
  open: boolean;
  onReload: () => void;
}

const ReloadPrompt: React.FC<Props> = ({ open, onReload }) => {
  const copy = useCopy();
  // Activating the new service worker can take several seconds before the page
  // unloads, so the click needs its own visible state until then.
  const [isReloading, setIsReloading] = useState(false);

  const handleReload = (): void => {
    if (isReloading) {
      return;
    }
    setIsReloading(true);
    onReload();
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      TransitionComponent={Slide}
      sx={{ zIndex: 2147483647, pointerEvents: "auto" }}
    >
      <Paper
        role="status"
        aria-live="polite"
        aria-busy={isReloading}
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          gap: 1.75,
          width: { xs: "calc(100vw - 32px)", sm: 460 },
          px: 2,
          py: 1.75,
          borderRadius: 3,
          bgcolor: "#18181b",
          color: "#fafafa",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.35)",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            width: 36,
            height: 36,
            borderRadius: 2,
            background: VERSION_UPDATE_PROMPT_GRADIENT,
          }}
        >
          <Sparkles size={18} color="#ffffff" aria-hidden />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4 }}>
            {copy.text(isReloading ? COMMON_COPY.versionUpdate.reloadingTitle : COMMON_COPY.versionUpdate.title)}
          </Typography>
          <Typography sx={{ fontSize: 13, lineHeight: 1.4, color: "#a1a1aa" }}>
            {copy.text(
              isReloading ? COMMON_COPY.versionUpdate.reloadingDescription : COMMON_COPY.versionUpdate.description
            )}
          </Typography>
        </Box>

        <Button
          onClick={handleReload}
          disabled={isReloading}
          disableElevation
          variant="contained"
          size="small"
          startIcon={
            isReloading ? (
              <Box
                component="span"
                sx={{ display: "inline-flex", animation: `${VERSION_UPDATE_PROMPT_SPIN} 1s linear infinite` }}
              >
                <Loader2 size={16} aria-hidden />
              </Box>
            ) : (
              <RotateCw size={16} aria-hidden />
            )
          }
          sx={{
            flexShrink: 0,
            px: 1.75,
            borderRadius: 2,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 600,
            bgcolor: "#fafafa",
            color: "#18181b",
            "&:hover": { bgcolor: "#e4e4e7" },
            "&.Mui-disabled": { bgcolor: "rgba(250, 250, 250, 0.16)", color: "#fafafa" },
          }}
        >
          {copy.text(COMMON_COPY.versionUpdate.reloadNow)}
        </Button>

        {isReloading && (
          <LinearProgress
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 2,
              bgcolor: "transparent",
              "& .MuiLinearProgress-bar": { background: VERSION_UPDATE_PROMPT_GRADIENT },
            }}
          />
        )}
      </Paper>
    </Snackbar>
  );
};

export default ReloadPrompt;
